/// <amd-module name="Repos/RepoDropdown/Components/RepositoryDropdown" />
import { ArtifactDropdown, renderSelectedItem } from "Favorites/Dropdown/ArtifactDropdown";
import * as React from "react";
import * as Resources from "Repos/RepoDropdown/Resources";
import { format } from "VSS/Core/Util/String";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { DropdownExpandableButton, IDropdownExpandableButtonProps } from "VSSUI/Dropdown";
import { Observer } from "VSSUI/Observer";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IRepositoryDropdownItem, RepositoryDropdownProvider } from "../RepositoryDropdownProvider";
import { IRepositoryDropdownProps } from "./RepositoryDropdown.Props";
import { ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";

export class RepositoryDropdown extends VssComponent<IRepositoryDropdownProps, {}> {
    private itemProvider: RepositoryDropdownProvider;
    private dropdown = React.createRef<ArtifactDropdown<IRepositoryDropdownItem>>();
    private dropdownSelection: ListSelection;
    private hasMadeInitialSelection = false;

    constructor(props: IRepositoryDropdownProps, context: IVssComponentContext) {
        super(props, context);

        this.itemProvider = new RepositoryDropdownProvider(context.pageContext, {
            ...props.providerOptions,
            onRepositoriesLoaded: props.onRepositoriesLoaded
        });
        this.dropdownSelection = props.dropdownSelection ?? new DropdownSelection();
    }

    public render(): JSX.Element | null {
        const { actions, className, onSelect, useDefaultCalloutWidth, onCollapse } = this.props;
        const { getArtifactHref } = this.props.providerOptions;
        
        return (
            <Observer itemProvider={{ observableValue: this.itemProvider, filter: this.onItemsUpdated }} selection={this.dropdownSelection}>
                {() => {
                    const ariaLabel = format(
                        Resources.RepositoryDropdownAriaLabel,
                        this.dropdownSelection.value.length
                            ? this.itemProvider.value[this.dropdownSelection.value[0].beginIndex].text
                            : this.props.selectedRepositoryItem
                            ? this.props.selectedRepositoryItem.name
                            : ""
                    );
                    if(this.props.getItemProvider){
                        this.props.getItemProvider(this.itemProvider.value);
                    }
                    const selectedItemsCount = this.props.selectedRepositoryItem ? 1 : this.props.selectedRepositoryItems ? this.props.selectedRepositoryItems.length : undefined;
                    return (
                        <ArtifactDropdown<IRepositoryDropdownItem>
                            ariaLabel={ariaLabel}
                            ref={this.dropdown}
                            onSelect={onSelect}
                            onCollapse={onCollapse}
                            otherActions={actions}
                            items={this.itemProvider}
                            getArtifactHref={getArtifactHref}
                            selection={this.dropdownSelection}
                            calloutContentClassName="repository-selector-callout"
                            className={css(className, "repository-selector scroll-hidden")}
                            width={this.getCalloutWidth(useDefaultCalloutWidth)}
                            filterPlaceholderText={Resources.SearchRepositoriesWatermark}
                            renderExpandable={(props: IDropdownExpandableButtonProps) => (
                                <DropdownExpandableButton {...props} subtle={true} style={{width: "100%"}}>
                                    <Observer selection={this.dropdownSelection}>
                                        {() =>
                                            (this.dropdownSelection.selectedCount === 0 && (this.props.selectedRepositoryItem || this.props.selectedRepositoryItems))
                                                ? renderSelectedItem(
                                                    this.itemProvider.getListItem(this.props.selectedRepositoryItem ?? this.props.selectedRepositoryItems![0]), 
                                                    selectedItemsCount
                                                )
                                                : null
                                        }
                                    </Observer>
                                </DropdownExpandableButton>
                            )}
                        />
                    );
                }}
            </Observer>
        );
    }

    private getCalloutWidth = (useDefaultCalloutWidth: boolean | undefined) : number | undefined => {
        if(useDefaultCalloutWidth || useDefaultCalloutWidth == undefined){
            return 480;
        }
        else{
            return undefined;
        }
    }

    public componentWillUnmount(): void {
        this.itemProvider.dispose();
    }

    public expand(): void {
        if (this.dropdown.current) {
            this.dropdown.current.expand();
        }
    }

    public focus(): void {
        if (this.dropdown.current) {
            this.dropdown.current.focus();
        }
    }

    private onItemsUpdated = () => {
        if (!this.hasMadeInitialSelection) {
            if(this.props.selectedRepositoryItem)
                this.makeInitialSelection();
            else if(this.props.selectedRepositoryItems)
                this.makeInitialSelections();
        }
        return false;
    };

    private makeInitialSelection = () => {
        if (this.itemProvider.value.length > 0 && this.props.selectedRepositoryItem) {
            // Select the item with the matching id
            for (let i = 0; i < this.itemProvider.value.length; i++) {
                if (this.itemProvider.value[i].data && this.itemProvider.value[i].data!.id === this.props.selectedRepositoryItem.id) {
                    this.dropdownSelection.select(i);
                    this.hasMadeInitialSelection = true;
                    return;
                }
            }
        }
    };

    private makeInitialSelections = () => {
        if (this.itemProvider.value.length > 0 && this.props.selectedRepositoryItems) {
            // Select the items with the matching id
            for (let i = 0; i < this.itemProvider.value.length; i++) {
                if (this.itemProvider.value[i].data && this.props.selectedRepositoryItems.some(repo => repo.id === this.itemProvider.value[i].data!.id)) {
                    this.dropdownSelection.select(i,undefined,true,true);
                }
            }
            this.hasMadeInitialSelection = true;
        }
    };
}